import * as React from 'react'
import { useFoundernestAdminTheme } from 'theme'
import { Testable } from 'utils-next/types'

import { DatePicker, DatePickerProps, LocalizationProvider } from '@mui/lab'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import { TextField as TextFieldMui, alpha } from '@mui/material'

export const MIN_YEAR = 1900
export const MAX_YEAR = new Date().getUTCFullYear()

export type YearPickerProps = {
  value: Date | null
  name?: string
  size?: number
  helperText?: string
  placeholder?: string
  onChange: (date: Event | null, keyboardInputValue?: string | undefined) => void
  error?: boolean
} & Omit<DatePickerProps, 'renderInput'> &
  Testable

const parseDate = (date: Date | string) => (typeof date === 'string' ? new Date(date) : date)

export const YearPicker = React.forwardRef<HTMLDivElement, YearPickerProps>((props, ref) => {
  const {
    value,
    name,
    size,
    error,
    maxDate,
    minDate,
    helperText,
    'data-testid': dataTestId,
    placeholder,
    onChange,
    ...rest
  } = props
  const theme = useFoundernestAdminTheme()
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        {...rest}
        ref={ref}
        views={['year']}
        value={value}
        onChange={onChange}
        minDate={parseDate(minDate as Date)}
        maxDate={parseDate(maxDate as Date)}
        renderInput={(params) => {
          delete params.inputProps?.placeholder

          return (
            <TextFieldMui
              sx={{
                svg: { color: theme.palette.primary.main },
                button: { '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) } },
              }}
              name={name}
              data-testid={dataTestId}
              placeholder={placeholder}
              color="primary"
              helperText={helperText}
              variant="standard"
              {...params}
              error={error}
              inputProps={{ placeholder: placeholder, ...params.inputProps }}
            />
          )
        }}
      />
    </LocalizationProvider>
  )
})

YearPicker.displayName = 'YearPicker'
